'use client';

import type {
  ErrorContext,
  SuggestionSelectionProps
} from '@carvana/merch-search-bar';
import MerchSearchBar from '@carvana/merch-search-bar';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile, useIsTablet } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import { generateErrorLogger } from '~/utilities/loggers';
import NotFoundSearchBarOverlay from './NotFoundSearchBarOverlay';

const NotFoundSearchBarModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "NotFoundSearchBarModal" */ './NotFoundSearchBarModal'
    ),
  { ssr: false }
);

export default function NotFoundSearchBar() {
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { state: geolocationState = '' } = useUserLocation();

  const onError = useCallback((error: Error, context: ErrorContext) => {
    generateErrorLogger(
      `NotFoundSearchBar - ${context.location}`,
      context
    )(error);
  }, []);

  const onSuggestionSelection = useCallback(
    ({ url }: SuggestionSelectionProps) => {
      router.push(url);
    },
    [router]
  );

  const searchBarProps = {
    apiBaseUrl: apikUrl,
    cdnBaseUrl: assetsCdnUrl,
    classes: { modalButton: 'h-[48px]' },
    disableScrollLock: true,
    geolocationState,
    inputValue,
    isMobile,
    isTablet,
    onCloseModal: () => setIsModalOpen(false),
    onError,
    onFocus: setIsFocused,
    onShowModalClick: () => setIsModalOpen(true),
    onSuggestionSelection,
    refSource: 'error-page',
    setInputValue,
    showButton: isMobile,
    size: 'lg' as const,
    trackEvent
  };

  return (
    <>
      <NotFoundSearchBarOverlay isVisible={isFocused} />
      <div
        className="relative z-30 text-left"
        data-testid="not-found-search-bar"
      >
        <MerchSearchBar {...searchBarProps} />
        {isMobile && (
          <NotFoundSearchBarModal
            {...searchBarProps}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    </>
  );
}
